import React, { useContext, useState } from 'react';
import {
  Box,
  Avatar,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Text,
  Badge,
  HStack,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Link,
  Tooltip,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from './UserContext';
import { usePlanLimits } from '../hooks/usePlanLimits';

const UserMenu = () => {
  const { userDetails, loading } = useContext(UserContext);
  const { planStatus } = usePlanLimits();
  const navigate = useNavigate();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  if (loading) {
    return <Spinner size="sm" />;
  }

  if (!userDetails) {
    return null;
  }

  const fullName = [userDetails.first_name, userDetails.last_name]
    .filter(Boolean)
    .join(' ') || 'User';

  const email = userDetails.email || '';
  const isPro = planStatus?.planType === 'pro' || userDetails?.plan_type === 'pro';

  const handleSignOut = () => {
    localStorage.removeItem('token');
    window.location.href = '/';
  };

  return (
    <Box
      p={1}
      display="flex"
      flexDirection="column"
      alignItems="center"
    >
      <Menu placement="bottom-end" offset={[0, 8]}>
          <Tooltip label="Your profile & settings" hasArrow placement="bottom" openDelay={500}>
          <MenuButton
            as={Box}
            cursor="pointer"
            _hover={{ opacity: 0.8 }}
            transition="opacity 0.15s"
          >
            <Box position="relative" display="inline-flex">
              <Avatar
                size="sm"
                name={userDetails.first_name}
                bg="brand.500"
                color="white"
              />
            </Box>
          </MenuButton>
          </Tooltip>

        <MenuList
          minW="240px"
          py={0}
          shadow="lg"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
        >
          {/* Header */}
          <Box px={4} py={3} borderBottom="1px solid" borderColor="gray.100">
            <HStack spacing={3} align="flex-start">
              <Avatar
                size="sm"
                name={userDetails.first_name}
                bg="brand.500"
                color="white"
              />
              <VStack align="start" spacing={0} flex={1}>
                <HStack spacing={2}>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                    {fullName}
                  </Text>
                  <Badge
                    colorScheme={isPro ? 'purple' : 'gray'}
                    fontSize="xs"
                    px={2}
                    py={0.5}
                    borderRadius="full"
                  >
                    {isPro ? 'Pro' : 'Free'}
                  </Badge>
                </HStack>
                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                  {email}
                </Text>
              </VStack>
            </HStack>
          </Box>

          {/* Primary items */}
          <Box py={1}>
            <MenuItem
              fontSize="sm"
              py={2}
              px={4}
              onClick={() => navigate('/profile')}
              _hover={{ bg: 'gray.50' }}
            >
              <HStack spacing={3}>
                <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#718096' }}>
                  person
                </span>
                <Text>Profile</Text>
              </HStack>
            </MenuItem>


            <MenuItem
              fontSize="sm"
              py={2}
              px={4}
              onClick={() => navigate('/account')}
              _hover={{ bg: 'gray.50' }}
            >
              <HStack spacing={3}>
                <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#718096' }}>
                  tune
                </span>
                <Text>Settings</Text>
              </HStack>
            </MenuItem>

            <MenuItem
              fontSize="sm"
              py={2}
              px={4}
              onClick={() => navigate('/plan-usage')}
              _hover={{ bg: 'gray.50' }}
            >
              <HStack spacing={3}>
                <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#718096' }}>
                  bar_chart
                </span>
                <Text>Plan & Usage</Text>
              </HStack>
            </MenuItem>

            <MenuItem
              fontSize="sm"
              py={2}
              px={4}
              onClick={() => navigate('/connectors')}
              _hover={{ bg: 'gray.50' }}
            >
              <HStack spacing={3}>
                <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#718096' }}>
                  cable
                </span>
                <Text>Connect your data</Text>
              </HStack>
            </MenuItem>
          </Box>

          <MenuDivider my={0} />

          {/* Secondary items */}
          <Box py={1}>
            <MenuItem
              fontSize="sm"
              py={2}
              px={4}
              onClick={() => window.open('/docs/create-your-first-plan', '_blank')}
              _hover={{ bg: 'gray.50' }}
            >
              <HStack spacing={3}>
                <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#718096' }}>
                  help_outline
                </span>
                <Text>Help</Text>
              </HStack>
            </MenuItem>

            <MenuItem
              fontSize="sm"
              py={2}
              px={4}
              onClick={() => setIsFeedbackOpen(true)}
              _hover={{ bg: 'gray.50' }}
            >
              <HStack spacing={3}>
                <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#718096' }}>
                  feedback
                </span>
                <Text>Share Ideas or Bugs</Text>
              </HStack>
            </MenuItem>
          </Box>

          <MenuDivider my={0} />

          {/* Sign out */}
          <Box py={1}>
            <MenuItem
              fontSize="sm"
              py={2}
              px={4}
              onClick={handleSignOut}
              _hover={{ bg: 'gray.50' }}
              color="red.500"
            >
              <HStack spacing={3}>
                <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#E53E3E' }}>
                  logout
                </span>
                <Text>Sign out</Text>
              </HStack>
            </MenuItem>
          </Box>

          {/* Footer links */}
          <Box
            px={4}
            py={2}
            borderTop="1px solid"
            borderColor="gray.100"
            bg="gray.50"
          >
            <HStack spacing={3}>
              <Link
                fontSize="xs"
                color="gray.400"
                _hover={{ color: 'gray.600' }}
                onClick={() => navigate('/data-privacy')}
                cursor="pointer"
              >
                Data & Privacy
              </Link>
              <Text fontSize="xs" color="gray.300">·</Text>
              <Link
                fontSize="xs"
                color="gray.400"
                _hover={{ color: 'gray.600' }}
                onClick={() => navigate('/terms')}
                cursor="pointer"
              >
                Terms
              </Link>
            </HStack>
          </Box>
        </MenuList>
      </Menu>

      <Modal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent maxW="800px" height="650px">
          <ModalCloseButton />
          <ModalBody>
            <iframe
              src="https://feedback.userreport.com/396a5292-fd09-4f4d-8a7b-caa5900bd2ce/"
              width="100%"
              height="600px"
              style={{ border: "none" }}
              title="Feedback Form"
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default UserMenu;
